import React, { useEffect, useRef, useState } from 'react';

interface PinnedContainerProps {
  position: 'top' | 'bottom';
  children: React.ReactNode;
  className?: string;
}

/**
 * Fixed positioning container with mobile keyboard support via visualViewport API.
 * Creates a spacer element to prevent content overlap.
 */
export const PinnedContainer: React.FC<PinnedContainerProps> = ({
  position,
  children,
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [keyboardOffset, setKeyboardOffset] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Track container height changes with ResizeObserver
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(container);

    // Handle mobile keyboard via visualViewport API
    const handleViewportResize = () => {
      if (window.visualViewport && position === 'bottom') {
        const windowHeight = window.innerHeight;
        const viewportHeight = window.visualViewport.height;
        const keyboardHeight = windowHeight - viewportHeight;

        // Only adjust if keyboard is significantly open (> 100px)
        setKeyboardOffset(keyboardHeight > 100 ? keyboardHeight : 0);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
      handleViewportResize(); // Initial check
    }

    return () => {
      resizeObserver.disconnect();
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
      }
    };
  }, [position]);

  const positionClass = position === 'top' ? 'pinned-container--top' : 'pinned-container--bottom';

  const containerStyle: React.CSSProperties = position === 'bottom' && keyboardOffset > 0
    ? { bottom: keyboardOffset }
    : {};

  return (
    <>
      <div
        ref={containerRef}
        className={`pinned-container ${positionClass} ${className}`.trim()}
        style={containerStyle}
      >
        {children}
      </div>
      {/* Spacer to prevent content overlap */}
      <div
        style={{
          height: containerHeight,
          flexShrink: 0,
          ...(position === 'top' ? {} : { marginTop: 'auto' })
        }}
        aria-hidden="true"
      />
    </>
  );
};

export default PinnedContainer;
